import { IconCalendar } from '@tabler/icons-react'

import { formatNumber } from '@/utils'

interface KPIGoalChartProps {
  trailingGoalData: number
  currentData: number
  kpiGoalData: number
  unit: string
  remainingDaysInQuarter: number
}

interface DataPoint {
  type: string
  value: number
}

function getTitle(type: string) {
  switch (type) {
    case 'trailingGoal':
      return 'Trailing Goal'
    case 'current':
      return 'Current'
    case 'kpiGoal':
      return 'KPI Goal'
    default:
      return ''
  }
}

function getColor(type: string) {
  switch (type) {
    case 'trailingGoal':
      return 'border-green-500'
    case 'current':
      return 'border-orange-500'
    case 'kpiGoal':
      return 'border-blue-500'
    default:
      return 'gray'
  }
}

function getLineColor(type: string) {
  switch (type) {
    case 'trailingGoal':
      return 'bg-green-500'
    case 'current':
      return 'bg-orange-500'
    case 'kpiGoal':
      return 'bg-blue-500'
    default:
      return 'gray'
  }
}

function KPIGoalChart({
  trailingGoalData,
  currentData,
  kpiGoalData,
  unit,
  remainingDaysInQuarter,
}: KPIGoalChartProps) {
  // Calculate min, max, and mid of the data
  const dataPoints = {
    trailingGoal: trailingGoalData,
    current: currentData,
    kpiGoal: kpiGoalData,
  }
  const minData: DataPoint = {
    type: Object.entries(dataPoints).reduce(
      (minKey, [key, val]) =>
        val === Math.min(...Object.values(dataPoints)) ? key : minKey,
      Object.keys(dataPoints)[0]
    ) as string,
    value: Math.min(...Object.values(dataPoints)),
  }
  const maxData: DataPoint = {
    type: Object.entries(dataPoints).reduce(
      (maxKey, [key, val]) =>
        val === Math.max(...Object.values(dataPoints)) ? key : maxKey,
      Object.keys(dataPoints)[0]
    ) as string,
    value: Math.max(...Object.values(dataPoints)),
  }
  const midData: DataPoint = {
    type: Object.entries(dataPoints).reduce(
      (midKey, [key, val]) =>
        val !== minData.value && val !== maxData.value ? key : midKey,
      Object.keys(dataPoints)[0]
    ) as string,
    value:
      Object.values(dataPoints).find(
        (value) => value !== minData.value && value !== maxData.value
      ) || 0,
  }

  // Determine appropriate low and high values for the chart
  // Add padding to make sure all data points are visible within the range
  const padding = (maxData.value - minData.value) * 0.2 // 20% padding
  const low = Math.round(Math.max(0, minData.value - padding))
  const high = Math.round(maxData.value + padding)

  // Calculate positions as percentages
  const range = high - low
  const minPosition = ((minData.value - low) / range) * 100
  const midPosition = ((midData.value - low) / range) * 100
  const maxPosition = ((maxData.value - low) / range) * 100

  return (
    <div>
      <div className="text-card-foreground px-4 flex justify-center items-center">
        <div className="flex flex-col items-center relative w-full max-w-md">
          {/* Top section - Max indicator */}
          <div
            className="absolute"
            style={{
              left: `${maxPosition}%`,
              top: '0',
              transform: 'translateX(-50%)',
            }}
          >
            <div className="flex flex-col items-center">
              <GoalBox value={maxData.value} unit={unit} type={maxData.type} />
              {/* Vertical line connecting to the slider */}
              <div
                className={`w-[3px] ${getLineColor(maxData.type)} h-[30px]`}
              ></div>
            </div>
          </div>

          {/* Top section - Min value */}
          <div
            className="absolute"
            style={{
              left: `${minPosition}%`,
              top: '0',
              transform: 'translateX(-50%)',
            }}
          >
            <div className="flex flex-col items-center">
              <GoalBox value={minData.value} unit={unit} type={minData.type} />
              {/* Vertical line connecting to the slider */}
              <div
                className={`w-[3px] ${getLineColor(minData.type)} h-[30px]`}
              ></div>
            </div>
          </div>

          {/* Middle section - Slider track */}
          <div className="w-full relative h-1 bg-gray-300 rounded-full my-4 mt-20">
            {/* Previous position marker */}
            <div
              className={`absolute w-4 h-4 rounded-full -mt-1.5 -ml-0.5 ${getLineColor(minData.type)}`}
              style={{ left: `calc(${minPosition}% - 6px)` }}
            />

            {/* Current position marker */}
            <div
              className={`absolute w-4 h-4 rounded-full -mt-1.5 ${getLineColor(midData.type)}`}
              style={{ left: `calc(${midPosition}% - 8px)` }}
            />

            {/* Average position marker */}
            <div
              className={`absolute w-4 h-4 rounded-full -mt-1.5 -ml-0.5 ${getLineColor(maxData.type)}`}
              style={{ left: `calc(${maxPosition}% - 6px)` }}
            />
          </div>

          {/* Low and High labels */}
          <div className="flex justify-between w-full mt-20">
            <div>
              <div className="text-lg font-bold">
                {formatNumber(
                  low,
                  unit as 'money' | 'percentage' | 'number' | 'hour',
                  true
                )}
              </div>
              <div className="text-xs">Low</div>
            </div>

            <div>
              <div className="text-lg font-bold">
                {formatNumber(
                  high,
                  unit as 'money' | 'percentage' | 'number' | 'hour',
                  true
                )}
              </div>
              <div className="text-xs">High</div>
            </div>
          </div>

          {/* Bottom section - Current value */}
          <div
            className="absolute"
            style={{
              left: `${midPosition}%`,
              bottom: '60px',
              transform: 'translateX(-50%)',
            }}
          >
            <div className="flex flex-col items-center">
              {/* Vertical line connecting to the slider */}
              <div
                className={`w-[3px] ${getLineColor(midData.type)} h-[30px]`}
              ></div>
              <GoalBox value={midData.value} unit={unit} type={midData.type} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 mt-6 px-4 py-2 bg-muted/50 rounded-lg border border-border/50">
        <IconCalendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">
          <span className="font-semibold text-foreground">
            {remainingDaysInQuarter}
          </span>{' '}
          {remainingDaysInQuarter === 1 ? 'day' : 'days'} remaining in quarter
        </span>
      </div>
    </div>
  )
}

const GoalBox = ({
  value,
  unit,
  type,
}: {
  value: number
  unit: string
  type: string
}) => {
  return (
    <div
      className={`w-[80px] text-center p-0.5 rounded border-2 ${getColor(type)} bg-white`}
      style={{
        marginLeft: value === 0 ? '40px' : '0',
      }}
    >
      <span className="block text-xs font-bold">
        {formatNumber(
          value,
          unit as 'money' | 'percentage' | 'number' | 'hour',
          true
        )}
      </span>
      <span className="text-[10px]">{getTitle(type)}</span>
    </div>
  )
}

export default KPIGoalChart
